import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Palmtree, Filter, Edit, Plus, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FincaTab({ user, onLogout }) {
  const [lotes, setLotes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filtroLote, setFiltroLote] = useState('todos');
  const [filtroParcela, setFiltroParcela] = useState('todos');
  const [filtroCentro, setFiltroCentro] = useState('');
  
  // Modal de edición
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCentro, setEditingCentro] = useState(null);
  const [formData, setFormData] = useState({
    cant_zurcos_izquierda: 0,
    cant_matas_izquierdo: 0,
    cant_zurcos_derecha: 0,
    cant_matas_derecho: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [lotesRes, centrosRes] = await Promise.all([
        axios.get(`${API}/lotes`),
        axios.get(`${API}/centros`)
      ]);
      setLotes(lotesRes.data);
      setCentros(centrosRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de la finca');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCentro = (centro) => {
    setEditingCentro(centro);
    setFormData({
      parcela_numero: centro.parcela_numero || '',
      cant_zurcos_izquierda: centro.cant_zurcos_izquierda || 0,
      cant_matas_izquierdo: centro.cant_matas_izquierdo || 0,
      cant_zurcos_derecha: centro.cant_zurcos_derecha || 0,
      cant_matas_derecho: centro.cant_matas_derecho || 0,
    });
    setEditDialogOpen(true);
  };

  const handleSaveCentro = async () => {
    try {
      const updateData = {
        parcela_id: editingCentro.parcela_id,
        lote_id: editingCentro.lote_id,
        numero_centro: editingCentro.numero_centro,
        parcela_numero: parseInt(formData.parcela_numero) || null,
        cant_zurcos: (parseInt(formData.cant_zurcos_izquierda) || 0) + (parseInt(formData.cant_zurcos_derecha) || 0),
        cant_zurcos_izquierda: parseInt(formData.cant_zurcos_izquierda) || 0,
        cant_matas_izquierdo: parseInt(formData.cant_matas_izquierdo) || 0,
        cant_zurcos_derecha: parseInt(formData.cant_zurcos_derecha) || 0,
        cant_matas_derecho: parseInt(formData.cant_matas_derecho) || 0,
        total_matas: (parseInt(formData.cant_matas_izquierdo) || 0) + (parseInt(formData.cant_matas_derecho) || 0),
      };

      await axios.put(`${API}/centros/${editingCentro.id}`, updateData);

      toast.success('Centro actualizado exitosamente');
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar centro');
      console.error(error);
    }
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setEditingCentro(null);
  };

  // Obtener parcelas únicas (filtrar nulls)
  const parcelasUnicas = [...new Set(centros.map(c => c.parcela_numero).filter(p => p !== null && p !== undefined))].sort((a, b) => a - b);

  // Aplicar filtros
  const centrosFiltrados = centros.filter(centro => {
    if (filtroLote !== 'todos' && centro.lote_id !== filtroLote) return false;
    if (filtroParcela !== 'todos' && centro.parcela_numero !== parseInt(filtroParcela)) return false;
    if (filtroCentro && !centro.numero_centro.toString().includes(filtroCentro)) return false;
    return true;
  });

  // Calcular totales
  const totalMatasFiltradas = centrosFiltrados.reduce((sum, c) => sum + (c.total_matas || 0), 0);
  const totalMatasIzqFiltradas = centrosFiltrados.reduce((sum, c) => sum + (c.cant_matas_izquierdo || 0), 0);
  const totalMatasDerFiltradas = centrosFiltrados.reduce((sum, c) => sum + (c.cant_matas_derecho || 0), 0);

  // Campos automáticos en el formulario
  const totalZurcosFormulario = (formData.cant_zurcos_izquierda || 0) + (formData.cant_zurcos_derecha || 0);
  const totalMatasFormulario = (formData.cant_matas_izquierdo || 0) + (formData.cant_matas_derecho || 0);

  return (
    <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Cargando datos de la finca...</p>
        </div>
      ) : (
              <div className="space-y-6">
                {/* Header con resumen */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Palmtree className="w-8 h-8 text-emerald-600" />
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900">Gestión de Lotes y Parcelas</h1>
                      <p className="text-sm text-gray-500 mt-1">
                        {lotes.length} Lotes • {centros.length} Centros Fruteros
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Badge variant="outline" className="text-lg px-4 py-2">
                      Total Matas: {centros.reduce((sum, c) => sum + (c.total_matas || 0), 0).toLocaleString()}
                    </Badge>
                  </div>
                </div>

                {/* Filtros */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Filter className="w-5 h-5 mr-2" />
                      Filtros
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Lote</Label>
                        <Select value={filtroLote} onValueChange={setFiltroLote}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todos los lotes</SelectItem>
                            {lotes.map(lote => (
                              <SelectItem key={lote.id} value={lote.id}>
                                {lote.nombre} ({lote.total_centros})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Parcela</Label>
                        <Select value={filtroParcela} onValueChange={setFiltroParcela}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todas las parcelas</SelectItem>
                            {parcelasUnicas.map(parcela => (
                              <SelectItem key={parcela} value={parcela.toString()}>
                                Parcela {parcela}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Buscar Centro</Label>
                        <Input
                          placeholder="Ej: 5"
                          value={filtroCentro}
                          onChange={(e) => setFiltroCentro(e.target.value)}
                        />
                      </div>

                      <div className="flex items-end">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setFiltroLote('todos');
                            setFiltroParcela('todos');
                            setFiltroCentro('');
                          }}
                          className="w-full"
                        >
                          Limpiar Filtros
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 flex space-x-4 text-sm text-gray-600">
                      <span>Mostrando: <strong>{centrosFiltrados.length}</strong> centros</span>
                      <span>•</span>
                      <span>Total Matas: <strong>{totalMatasFiltradas.toLocaleString()}</strong></span>
                    </div>
                  </CardContent>
                </Card>

                {/* Tabla de Centros */}
                <Card>
                  <CardHeader>
                    <CardTitle>Centros Fruteros - Detalle Completo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead className="sticky top-0 bg-emerald-600 text-white z-10">
                          <tr>
                            <th className="border border-emerald-700 px-2 py-2">LOTE</th>
                            <th className="border border-emerald-700 px-2 py-2"># PARCELA</th>
                            <th className="border border-emerald-700 px-2 py-2"># CENTRO</th>
                            <th className="border border-emerald-700 px-2 py-2">ZURCOS TOTAL</th>
                            <th className="border border-emerald-700 px-2 py-2">ZURCOS IZQ</th>
                            <th className="border border-emerald-700 px-2 py-2">MATAS IZQ</th>
                            <th className="border border-emerald-700 px-2 py-2">ZURCOS DER</th>
                            <th className="border border-emerald-700 px-2 py-2">MATAS DER</th>
                            <th className="border border-emerald-700 px-2 py-2 font-bold">TOTAL MATAS</th>
                            <th className="border border-emerald-700 px-2 py-2">ACCIONES</th>
                          </tr>
                        </thead>
                        <tbody>
                          {centrosFiltrados.length === 0 ? (
                            <tr>
                              <td colSpan="10" className="text-center py-8 text-gray-500">
                                No se encontraron centros con los filtros aplicados
                              </td>
                            </tr>
                          ) : (
                            centrosFiltrados.map((centro, idx) => (
                              <tr
                                key={centro.id}
                                className={idx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}
                              >
                                <td className="border border-gray-300 px-2 py-2 text-center font-medium">
                                  {centro.lote_nombre}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center">
                                  {centro.parcela_numero}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center font-semibold">
                                  {centro.numero_centro}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center bg-yellow-50">
                                  {centro.cant_zurcos || 0}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center">
                                  {centro.cant_zurcos_izquierda || 0}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center text-blue-700 font-medium">
                                  {centro.cant_matas_izquierdo || 0}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center">
                                  {centro.cant_zurcos_derecha || 0}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center text-purple-700 font-medium">
                                  {centro.cant_matas_derecho || 0}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center bg-emerald-50 font-bold text-emerald-900">
                                  {centro.total_matas || 0}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-center">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditCentro(centro)}
                                    className="hover:bg-emerald-100"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                          {/* Fila de totales */}
                          {centrosFiltrados.length > 0 && (
                            <tr className="bg-emerald-100 font-bold sticky bottom-0">
                              <td colSpan="5" className="border border-emerald-700 px-2 py-2 text-right">
                                TOTALES:
                              </td>
                              <td className="border border-emerald-700 px-2 py-2 text-center text-blue-900">
                                {totalMatasIzqFiltradas.toLocaleString()}
                              </td>
                              <td className="border border-emerald-700 px-2 py-2"></td>
                              <td className="border border-emerald-700 px-2 py-2 text-center text-purple-900">
                                {totalMatasDerFiltradas.toLocaleString()}
                              </td>
                              <td className="border border-emerald-700 px-2 py-2 text-center bg-emerald-200 text-emerald-900 text-base">
                                {totalMatasFiltradas.toLocaleString()}
                              </td>
                              <td className="border border-emerald-700 px-2 py-2"></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Resumen estadístico */}
                    <div className="grid grid-cols-3 gap-4 mt-6">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="pt-6 text-center">
                          <p className="text-sm text-gray-600">Matas Izquierda</p>
                          <p className="text-2xl font-bold text-blue-700">
                            {totalMatasIzqFiltradas.toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="pt-6 text-center">
                          <p className="text-sm text-gray-600">Matas Derecha</p>
                          <p className="text-2xl font-bold text-purple-700">
                            {totalMatasDerFiltradas.toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-emerald-50 border-emerald-200">
                        <CardContent className="pt-6 text-center">
                          <p className="text-sm text-gray-600">Total Matas</p>
                          <p className="text-2xl font-bold text-emerald-700">
                            {totalMatasFiltradas.toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

        {/* Modal de Edición */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Edit className="w-5 h-5 mr-2 text-emerald-600" />
                Editar Centro - {editingCentro?.lote_nombre} / Parcela {editingCentro?.parcela_numero} / Centro {editingCentro?.numero_centro}
              </DialogTitle>
              <DialogDescription>
                Modifica los datos del centro. Los totales se calculan automáticamente.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Campo # Parcela */}
              <div>
                <Label># Parcela *</Label>
                <Select 
                  value={formData.parcela_numero?.toString() || ''} 
                  onValueChange={(v) => setFormData({ ...formData, parcela_numero: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione parcela (1-11)" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(num => (
                      <SelectItem key={num} value={num.toString()}>
                        Parcela {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Selecciona la parcela a la que pertenece este centro (1-11)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Lado Izquierdo */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-base text-blue-900">Lado Izquierdo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Cant. Zurcos Izquierda</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.cant_zurcos_izquierda}
                        onChange={(e) => setFormData({ ...formData, cant_zurcos_izquierda: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label>Cant. Matas Izquierda</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.cant_matas_izquierdo}
                        onChange={(e) => setFormData({ ...formData, cant_matas_izquierdo: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Lado Derecho */}
                <Card className="bg-purple-50 border-purple-200">
                  <CardHeader>
                    <CardTitle className="text-base text-purple-900">Lado Derecho</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Cant. Zurcos Derecha</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.cant_zurcos_derecha}
                        onChange={(e) => setFormData({ ...formData, cant_zurcos_derecha: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label>Cant. Matas Derecha</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.cant_matas_derecho}
                        onChange={(e) => setFormData({ ...formData, cant_matas_derecho: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Totales Automáticos */}
              <Card className="bg-emerald-50 border-emerald-300">
                <CardHeader>
                  <CardTitle className="text-base text-emerald-900">Totales (Automáticos)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Total Zurcos</Label>
                      <Input
                        type="number"
                        value={totalZurcosFormulario}
                        disabled
                        className="bg-yellow-100 font-bold"
                      />
                    </div>
                    <div>
                      <Label>Total Matas</Label>
                      <Input
                        type="number"
                        value={totalMatasFormulario}
                        disabled
                        className="bg-emerald-100 font-bold"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button onClick={handleSaveCentro} className="bg-emerald-600 hover:bg-emerald-700">
                <Save className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}
